// src/hooks/usePermissions.ts
//
// Thin client-side mirror of the server RBAC table (spec §3). This exists
// ONLY to hide/disable UI affordances early so members aren't shown dead
// buttons — the backend re-checks every one of these independently and is
// the real authorization boundary. Never add a permission here that isn't
// also enforced server-side (see mocks/api.ts, which imports the same
// permissions/permissions.ts engine).
//
// `can(permission)` is the general-purpose check new code should use.
// The named booleans below it (isExecOrAbove, canManageEvent, etc.) exist
// for the screens written before the granular permission system existed —
// they're now DERIVED from `can(...)` / the shared engine instead of raw
// role-tier comparisons, so behavior is unchanged but the underlying model
// is real permissions, not hardcoded role checks.

import { useAuthStore } from "../store/useAuthStore";
import { usePermissionsStore } from "../store/usePermissionsStore";
import { useOfficePermissionsStore } from "../store/useOfficePermissionsStore";
import {
  hasPermission,
  isExecOrAbove as isExecOrAboveOf,
  isSuperAdmin as isSuperAdminOf,
  hasAnyManagementAccess,
  hasScopedManagementAccess,
} from "../permissions/permissions";
import type { Permission } from "../types";

/** Permissions whose screens live under /admin (see navModel.ts). */
const ADMIN_AREA_PERMISSIONS: Permission[] = [
  "chapters.manageInvites",
  "feedback.view",
  "semesters.manage",
  "attendance.viewReport",
  "dues.manage",
  "finance.manage",
];

interface ScopedEvent {
  committeeId?: string | null;
  committeeIds?: string[];
  createdById?: string;
}

interface DelegatableEvent extends ScopedEvent {
  attendanceDelegates?: { userId: string }[];
}

export function usePermissions() {
  const user = useAuthStore((s) => s.user);
  const presets = usePermissionsStore((s) => s.presets);
  const officePresets = useOfficePermissionsStore((s) => s.presets);

  const role = user?.role ?? null;
  const office = user?.office ?? null;

  const isSuperAdmin = isSuperAdminOf(role);
  const isExecOrAbove = isExecOrAboveOf(role);

  // Broad "has some management responsibility" signal — Exec+ or an ACTIVE
  // member who chairs at least one committee. Replaces the removed OFFICER
  // role tier (committee chairs used to literally hold that role; now
  // chairing is tracked via committeeChairOf, independent of role).
  const isOfficerOrAbove = hasAnyManagementAccess({
    role,
    status: user?.status ?? null,
    committeeChairOf: user?.committeeChairOf ?? [],
  });

  // Named exec-board offices — ADDITIVE to the permission checks below,
  // never subtractive. SUPER_ADMIN always bypasses regardless of office.
  const isViceRegentOrAdmin = isSuperAdmin || office === "VICE_REGENT";
  const isScribeOrAdmin = isSuperAdmin || office === "SCRIBE";
  const isTreasurerOrAdmin = isSuperAdmin || office === "TREASURER";

  /** General-purpose granular permission check — use this for any new code. */
  function can(permission: Permission): boolean {
    return hasPermission(role, presets, permission, office, officePresets);
  }

  /**
   * Can this user manage (edit, delete, add delegates to) this event? Mirrors
   * backend/lib/eventAccess.ts: events.edit, the event's creator, or a chair
   * of any committee hosting it.
   */
  function canManageEvent(event: ScopedEvent): boolean {
    if (!user) return false;
    if (can("events.edit")) return true;
    if (event.createdById && event.createdById === user.id) return true;
    const hosts = event.committeeIds ?? (event.committeeId ? [event.committeeId] : []);
    return hosts.some((committeeId) =>
      hasScopedManagementAccess({
        role,
        status: user.status,
        committeeChairOf: user.committeeChairOf,
        committeeId,
      })
    );
  }

  /**
   * Can this user see the RSVP/attendance roster, mark or remove attendance,
   * and show the check-in code? Without an event: only the chapter-wide
   * grant (the Scribe's whole job). With one: also anyone who can manage it
   * and its delegates — a delegate runs the door without editing the event.
   */
  function canManageAttendance(event?: DelegatableEvent): boolean {
    if (isScribeOrAdmin || can("attendance.edit")) return true;
    if (!event || !user) return false;
    return canManageEvent(event) || (event.attendanceDelegates ?? []).some((d) => d.userId === user.id);
  }

  /** Same access as canManageAttendance; kept for the check-in screens. */
  function canGenerateCheckIn(event: DelegatableEvent): boolean {
    return canManageAttendance(event);
  }

  // The admin area only earns a nav slot when it holds something this user
  // can actually use. Chairing a committee alone isn't enough — everything a
  // chair manages lives on the committee and event pages, and the roster the
  // admin home leads with is Exec+ server-side.
  const canViewAdminPanel =
    isExecOrAbove || isTreasurerOrAdmin || ADMIN_AREA_PERMISSIONS.some((permission) => can(permission));

  return {
    role,
    office,
    isOfficerOrAbove,
    isExecOrAbove,
    isSuperAdmin,
    isViceRegentOrAdmin,
    isScribeOrAdmin,
    isTreasurerOrAdmin,
    can,
    canManageEvent,
    canManageAttendance,
    canGenerateCheckIn,
    canViewAdminPanel,
    canViewRoster: isExecOrAbove,
    // Exec+, or a chair — whose events must include a committee they chair.
    canCreateEvents: can("events.create") || (user?.committeeChairOf ?? []).length > 0,
  };
}
